import ButtonBase from '@/global/components/Button/ButtonBase'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/global/components/ui/popover'
import { Tooltip } from '@/global/components/ui/tooltip'
import dayjs from 'dayjs'
import { Button } from 'global/components/Button'
import SelectCompetencia from 'global/components/Selects/SelectCompetencia'
import { CalculatorIcon, CodeXmlIcon } from 'lucide-react'
import React from 'react'
import { calculo, espelhoPonto } from 'service/api'
import { toast } from 'sonner'

type Props = {
    empresaId: number
    funcionarioIds?: number[]
}

/** Gera lista de competências (YYYY-MM) de inicial até final, inclusivo. */
function competenciasNoIntervalo(inicial: string, final: string): string[] {
    const [ini, fim] = inicial <= final ? [inicial, final] : [final, inicial]
    const lista: string[] = []
    let cursor = dayjs(`${ini}-01`)
    const limite = dayjs(`${fim}-01`)
    while (cursor.isSame(limite) || cursor.isBefore(limite)) {
        lista.push(cursor.format('YYYY-MM'))
        cursor = cursor.add(1, 'month')
    }
    return lista
}

/**
 * Ferramenta dev-only (renderizar dentro de <DevelopmentOnly>): permite
 * escolher um intervalo de competências (inicial → final) e recalculá-las de
 * uma vez, ignorando se estão abertas ou fechadas. O backend (`calculo-ponto`)
 * já apaga os cálculos existentes antes de reprocessar e não valida
 * aberta/fechada, então basta disparar os POSTs contornando a trava de UI do
 * botão normal.
 *
 * Renderizado como botão compacto que abre um popup com o intervalo.
 */
export default function RecalcularCompetenciasDev({
    empresaId,
    funcionarioIds = [],
}: Props) {
    const [inicial, setInicial] = React.useState(() =>
        dayjs().format('YYYY-MM'),
    )
    const [final, setFinal] = React.useState(() => dayjs().format('YYYY-MM'))
    const [loading, setLoading] = React.useState(false)
    const [open, setOpen] = React.useState(false)

    const lista = competenciasNoIntervalo(inicial, final)

    const recalcular = async () => {
        if (!lista.length || !empresaId) return

        setLoading(true)
        const results = await Promise.allSettled(
            lista.map(c =>
                calculo.historico.post({
                    empresaId,
                    competencia: c,
                    baixarPdf: false,
                    baixarZip: false,
                    ...(funcionarioIds.length ? { funcionarioIds } : {}),
                }),
            ),
        )
        setLoading(false)

        const ok = results.filter(r => r.status === 'fulfilled').length
        const erro = results.length - ok

        await espelhoPonto.invalidateQueries()

        if (erro === 0)
            toast.success(`${ok} competência(s) enviada(s) para recálculo`)
        else toast.warning(`${ok} enviada(s), ${erro} com erro`)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <span>
                    <ButtonBase
                        className="border-dashed border-warning text-warning opacity-80"
                        variant="ghost"
                    >
                        <CodeXmlIcon />
                        Recalcular
                    </ButtonBase>
                </span>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                arrow={false}
                className="border-0 bg-transparent p-0 shadow-none"
            >
                <div className="flex flex-col gap-2 rounded-sm border border-dashed border-warning bg-muted p-3">
                    <span className="text-xs font-semibold text-warning">
                        Recalcular competências (ignora aberta/fechada)
                        {funcionarioIds.length > 0
                            ? ` · ${funcionarioIds.length} funcionário(s) selecionado(s)`
                            : ' · empresa inteira'}
                    </span>
                    <div className="flex items-end gap-2">
                        <SelectCompetencia
                            label="Competência inicial"
                            value={inicial}
                            onChange={setInicial}
                            hideButtons
                        />
                        <SelectCompetencia
                            label="Competência final"
                            value={final}
                            onChange={setFinal}
                            hideButtons
                        />
                    </div>
                    <Tooltip
                        title={
                            lista.length === 0
                                ? 'Selecione um intervalo válido'
                                : undefined
                        }
                    >
                        <span>
                            <Button
                                variant="contained"
                                startIcon={<CalculatorIcon />}
                                disabled={
                                    loading || lista.length === 0 || !empresaId
                                }
                                onClick={recalcular}
                            >
                                {loading
                                    ? 'Enviando...'
                                    : `Recalcular (${lista.length})`}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </PopoverContent>
        </Popover>
    )
}
